/**
 * Anime Jersey Combo Builder Logic
 * Conforms strictly to CRO development guidelines in AGENTS.md:
 * - Pure business logic functions separated from DOM
 * - Data attribute DOM access only
 * - Event delegation on document
 * - Defensive try/catch wrapping
 * - Zero global variables
 */

(function () {
  'use strict';

  /**
   * Pure Business Logic: Calculate bundle pricing and savings
   * @param {Array<{ price: number, comparePrice?: number }>} items
   * @param {number} discountPercent
   * @returns {{ originalTotal: number, discountedTotal: number, savings: number }}
   */
  function calculateBundlePricing(items, discountPercent) {
    if (!Array.isArray(items) || items.length === 0) {
      return { originalTotal: 0, discountedTotal: 0, savings: 0 };
    }

    const originalTotal = items.reduce(function (sum, item) {
      return sum + (Number(item.price) || 0);
    }, 0);

    const safeDiscount = Math.max(0, Math.min(100, Number(discountPercent) || 0));
    const discountFactor = (100 - safeDiscount) / 100;
    const discountedTotal = Math.round(originalTotal * discountFactor);
    const savings = originalTotal - discountedTotal;

    return {
      originalTotal: originalTotal,
      discountedTotal: discountedTotal,
      savings: savings
    };
  }

  /**
   * Pure Business Logic: Format currency string (INR default with Indian numbering format)
   * @param {number} centsOrAmount
   * @returns {string}
   */
  function formatCurrency(amount) {
    try {
      // If amount is in cents (> 10000), adjust if needed; in Shopify Liquid, prices passed can be in cents or base
      const value = amount > 10000 ? Math.round(amount / 100) : amount;
      return '₹' + value.toLocaleString('en-IN');
    } catch (e) {
      return '₹' + amount;
    }
  }

  /**
   * Pure Business Logic: Parse player string into separate name and number
   * Matches normal product add-to-cart parsing (e.g. "Isagi #11" -> { name: "ISAGI", number: "11" })
   * @param {string} playerString
   * @returns {{ name: string, number: string }}
   */
  function parsePlayerString(playerString) {
    if (!playerString || typeof playerString !== 'string') {
      return { name: '', number: '' };
    }

    const cleanString = playerString.trim();

    if (cleanString.includes('#')) {
      const parts = cleanString.split('#');
      const parsedName = (parts[0] || '').trim().toUpperCase();
      const parsedNumber = (parts[1] || '').replace(/[^0-9]/g, '').trim();
      return { name: parsedName, number: parsedNumber };
    }

    const numberMatch = cleanString.match(/^(.*?)\s*#?\s*(\d+)$/);
    if (numberMatch) {
      const matchedName = (numberMatch[1] || '').trim().toUpperCase();
      const matchedNumber = (numberMatch[2] || '').trim();
      return { name: matchedName, number: matchedNumber };
    }

    return { name: cleanString.toUpperCase(), number: '' };
  }

  /**
   * Centralized Controller State
   */
  const comboState = {
    discount1: 10,
    discount2: 15,
    discount3: 20,
    slots: [], // Array of selected product items
    activeModalProduct: null,
    selectedSize: '',
    selectedVariantId: '',
    selectedVariantPrice: 0,
    selectedPlayerPreset: '',
    customName: '',
    customNumber: ''
  };

  /**
   * Pure Business Logic: Get current discount percent based on item count
   * @param {number} count
   * @returns {number}
   */
  function getCurrentDiscount(count) {
    if (count >= 3) return comboState.discount3;
    if (count === 2) return comboState.discount2;
    if (count === 1) return comboState.discount1;
    return 0;
  }

  const STORAGE_KEY = 'weeb_combo_builder_slots';

  /**
   * Persist current combo slots to localStorage
   */
  function saveComboSlotsToStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(comboState.slots));
      }
    } catch (e) {
      // Storage quota or privacy restriction
    }
  }

  /**
   * Load saved combo slots from localStorage
   */
  function loadComboSlotsFromStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            comboState.slots = parsed.filter(function (item) {
              return item && item.variantId && item.title;
            });
          }
        }
      }
    } catch (e) {
      comboState.slots = [];
    }
  }

  /**
   * Clear saved combo slots from localStorage
   */
  function clearComboSlotsFromStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {}
  }

  /**
   * Updates the UI sticky dock display
   * @param {HTMLElement} root
   */
  function updateDockUI(root) {
    try {
      if (!root) return;

      const dock = root.querySelector('[data-combo-dock]');
      if (!dock) return;

      const count = comboState.slots.length;
      const currentDiscount = getCurrentDiscount(count);
      const pricing = calculateBundlePricing(comboState.slots, currentDiscount);

      // --- 1. Update Tiered Discount Meter ---
      const meterEl = dock.querySelector('[data-combo-meter]');
      if (meterEl) {
        const fillEl = meterEl.querySelector('[data-combo-meter-fill]');
        const msgEl = meterEl.querySelector('[data-combo-meter-msg]');
        const badgeEl = meterEl.querySelector('[data-combo-meter-badge]');

        let fillPercent = 0;
        let meterMsg = `Add 1 jersey to unlock ${comboState.discount1}% OFF`;
        let meterBadge = '0/3 UNLOCKED';

        if (count === 1) {
          fillPercent = 33.33;
          meterMsg = `${comboState.discount1}% OFF unlocked! Add 1 more for ${comboState.discount2}% OFF`;
          meterBadge = '1/3 UNLOCKED';
        } else if (count === 2) {
          fillPercent = 66.66;
          meterMsg = `${comboState.discount2}% OFF unlocked! Add 1 more for ${comboState.discount3}% OFF`;
          meterBadge = '2/3 UNLOCKED';
        } else if (count >= 3) {
          fillPercent = 100;
          meterMsg = `MAX SAVINGS UNLOCKED (${comboState.discount3}% OFF)!`;
          meterBadge = '3/3 UNLOCKED';
        }

        if (fillEl) {
          fillEl.style.width = `${fillPercent}%`;
        }
        if (msgEl) {
          msgEl.textContent = meterMsg;
        }
        if (badgeEl) {
          badgeEl.textContent = meterBadge;
        }

        // Toggle unlock states on step milestone nodes
        meterEl.classList.toggle('is-max-unlocked', count >= 3);
        const step1 = meterEl.querySelector('[data-meter-step="1"]');
        const step2 = meterEl.querySelector('[data-meter-step="2"]');
        const step3 = meterEl.querySelector('[data-meter-step="3"]');

        if (step1) step1.classList.toggle('is-unlocked', count >= 1);
        if (step2) step2.classList.toggle('is-unlocked', count >= 2);
        if (step3) step3.classList.toggle('is-unlocked', count >= 3);
      }

      // --- 2. Render Slots ---
      const slotsContainer = dock.querySelector('[data-combo-slots]');
      if (slotsContainer) {
        slotsContainer.innerHTML = '';

        // 1. Render all filled slots
        comboState.slots.forEach(function (item, i) {
          const slotEl = document.createElement('div');
          slotEl.className = 'combo-slot is-filled';
          slotEl.setAttribute('data-slot-index', i.toString());

          const sizeBadge = item.size ? `<span class="combo-slot__pill">Size: ${item.size}</span>` : '';
          const customName = item.playerName || item.customName;
          const numberText = item.playerNumber ? `#${item.playerNumber}` : '';
          const customBadge = customName ? `<span class="combo-slot__pill">${customName} ${numberText}</span>` : '';
          const priceText = item.price ? formatCurrency(item.price) : '';

          const thumbHtml = item.url
            ? `<a href="${item.url}" class="combo-slot__thumb-link"><img src="${item.image || ''}" alt="${item.title || ''}" class="combo-slot__thumb"></a>`
            : `<img src="${item.image || ''}" alt="${item.title || ''}" class="combo-slot__thumb">`;

          const titleHtml = item.url
            ? `<a href="${item.url}" class="combo-slot__title-link"><span class="combo-slot__title">${item.title}</span></a>`
            : `<span class="combo-slot__title">${item.title}</span>`;

          slotEl.innerHTML = `
            ${thumbHtml}
            <div class="combo-slot__info">
              <div class="combo-slot__header-row">
                ${titleHtml}
                ${priceText ? `<span class="combo-slot__price">${priceText}</span>` : ''}
              </div>
              <div class="combo-slot__meta">
                ${sizeBadge}
                ${customBadge}
              </div>
            </div>
            <button type="button" class="combo-slot__remove" data-action="remove-slot" data-index="${i}" aria-label="Remove item">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          `;
          slotsContainer.appendChild(slotEl);
        });

        // 2. If 0 items, show initial prompt slots
        if (count === 0) {
          const slot1 = document.createElement('div');
          slot1.className = 'combo-slot combo-slot--empty';
          slot1.setAttribute('data-action', 'scroll-to-products');
          slot1.innerHTML = `
            <div class="combo-slot__icon-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            </div>
            <div class="combo-slot__info">
              <div class="combo-slot__title">+ Pick Jersey 1</div>
              <div class="combo-slot__sub" style="color:var(--cb-gold);">Unlock ${comboState.discount1}% OFF</div>
            </div>
          `;
          slotsContainer.appendChild(slot1);

          const slot2 = document.createElement('div');
          slot2.className = 'combo-slot combo-slot--empty';
          slot2.setAttribute('data-action', 'scroll-to-products');
          slot2.innerHTML = `
            <div class="combo-slot__icon-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            </div>
            <div class="combo-slot__info">
              <div class="combo-slot__title">+ Pick Jersey 2</div>
              <div class="combo-slot__sub">Unlock ${comboState.discount2}% OFF</div>
            </div>
          `;
          slotsContainer.appendChild(slot2);
        } else {
          // Always provide an "+ Add Another" prompt so customer can freely add more
          const nextCount = count + 1;
          const nextOffer = nextCount >= 3
            ? `Unlock ${comboState.discount3}% OFF (Best Value)`
            : nextCount === 2
            ? `Unlock ${comboState.discount2}% OFF`
            : `${comboState.discount1}% OFF`;

          const addAnotherSlot = document.createElement('div');
          addAnotherSlot.className = 'combo-slot combo-slot--empty';
          addAnotherSlot.setAttribute('data-action', 'scroll-to-products');
          addAnotherSlot.innerHTML = `
            <div class="combo-slot__icon-box combo-slot__icon-box--accent">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            </div>
            <div class="combo-slot__info">
              <div class="combo-slot__title">+ Add Jersey #${nextCount}</div>
              <div class="combo-slot__sub" style="color:var(--cb-gold);">${nextOffer}</div>
            </div>
          `;
          slotsContainer.appendChild(addAnotherSlot);
        }
      }

      // --- 3. Update Pricing Display ---
      const priceTotalEl = dock.querySelector('[data-combo-total-price]');
      const priceCompareEl = dock.querySelector('[data-combo-compare-price]');
      const savingsTagEl = dock.querySelector('[data-combo-savings-tag]');
      const submitBtn = dock.querySelector('[data-combo-submit-btn]');

      const hasComboDiscount = currentDiscount > 0;
      const canSubmit = count >= 1;

      if (priceTotalEl) {
        priceTotalEl.textContent = formatCurrency(hasComboDiscount ? pricing.discountedTotal : pricing.originalTotal);
      }
      if (priceCompareEl) {
        if (hasComboDiscount && pricing.savings > 0) {
          priceCompareEl.textContent = formatCurrency(pricing.originalTotal);
          priceCompareEl.style.display = 'inline';
        } else {
          priceCompareEl.style.display = 'none';
        }
      }
      if (savingsTagEl) {
        if (count === 0) {
          savingsTagEl.textContent = `Pick 1 or more jerseys to unlock up to ${comboState.discount3}% OFF!`;
          savingsTagEl.style.color = 'var(--cb-text-muted)';
        } else if (count === 1) {
          savingsTagEl.textContent = `SAVED ${formatCurrency(pricing.savings)} (${comboState.discount1}% OFF)! Add 1 more for ${comboState.discount2}% OFF!`;
          savingsTagEl.style.color = '#00ff88';
        } else if (count === 2) {
          savingsTagEl.textContent = `SAVED ${formatCurrency(pricing.savings)} (${comboState.discount2}% OFF)! Add 1 more for ${comboState.discount3}% OFF!`;
          savingsTagEl.style.color = '#00ff88';
        } else {
          savingsTagEl.textContent = `SAVED ${formatCurrency(pricing.savings)} (${comboState.discount3}% OFF - BEST VALUE)!`;
          savingsTagEl.style.color = '#00ff88';
        }
        savingsTagEl.style.display = 'inline';
      }

      const countBadge = dock.querySelector('[data-combo-count-badge]');
      if (countBadge) {
        countBadge.textContent = `${count} ${count === 1 ? 'JERSEY' : 'JERSEYS'}`;
      }

      // Mobile expand bar updates
      const mobileCountEl = dock.querySelector('[data-combo-mobile-count]');
      const mobilePriceEl = dock.querySelector('[data-combo-mobile-price]');
      if (mobileCountEl) {
        mobileCountEl.textContent = `${count} ${count === 1 ? 'JERSEY' : 'JERSEYS'} SELECTED`;
      }
      if (mobilePriceEl) {
        mobilePriceEl.textContent = formatCurrency(hasComboDiscount ? pricing.discountedTotal : pricing.originalTotal);
      }

      if (submitBtn) {
        submitBtn.disabled = !canSubmit;
        const btnText = submitBtn.querySelector('[data-btn-text]');
        if (btnText) {
          if (count === 0) {
            btnText.textContent = 'PICK YOUR JERSEYS';
          } else if (count === 1) {
            btnText.textContent = `ADD 1 JERSEY • ${formatCurrency(pricing.discountedTotal)} (${comboState.discount1}% OFF)`;
          } else {
            btnText.textContent = `ADD ${count}-JERSEY COMBO • ${formatCurrency(pricing.discountedTotal)}`;
          }
        }
      }
    } catch (err) {
      console.error('[ComboBuilder] Error updating dock:', err);
    }
  }

  /**
   * Opens the Quick Customization Modal for a selected product
   * @param {HTMLElement} root
   * @param {Object} product
   */
  function openCustomizationModal(root, product) {
    try {
      if (!root || !product) return;

      const modal = root.querySelector('[data-combo-modal]');
      if (!modal) return;

      comboState.activeModalProduct = product;
      comboState.selectedPlayerPreset = '';
      comboState.customName = '';
      comboState.customNumber = '';

      // Populate preview
      const thumb = modal.querySelector('[data-modal-thumb]');
      const title = modal.querySelector('[data-modal-title]');
      const price = modal.querySelector('[data-modal-price]');

      if (thumb) thumb.src = product.image || '';
      if (title) title.textContent = product.title || '';

      // Render sizes
      const sizesContainer = modal.querySelector('[data-modal-sizes]');
      if (sizesContainer) {
        sizesContainer.innerHTML = '';
        const variants = product.variants || [];
        
        let initialSelectedVariant = variants[0];
        variants.forEach(function (variant, idx) {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'combo-size-pill' + (idx === 0 ? ' is-selected' : '');
          pill.setAttribute('data-variant-id', variant.id);
          pill.setAttribute('data-size-title', variant.title);
          if (variant.price) {
            pill.setAttribute('data-variant-price', variant.price);
          }
          pill.textContent = variant.title;
          sizesContainer.appendChild(pill);
        });

        comboState.selectedVariantId = initialSelectedVariant ? initialSelectedVariant.id : '';
        comboState.selectedSize = initialSelectedVariant ? initialSelectedVariant.title : '';
        comboState.selectedVariantPrice = initialSelectedVariant && initialSelectedVariant.price
          ? (Number(initialSelectedVariant.price) || 0)
          : (Number(product.price) || 0);
      } else {
        comboState.selectedVariantId = '';
        comboState.selectedSize = '';
        comboState.selectedVariantPrice = Number(product.price) || 0;
      }

      if (price) {
        price.textContent = formatCurrency(comboState.selectedVariantPrice);
      }

      // Render Preset Players
      const playersContainer = modal.querySelector('[data-modal-players]');
      const customFields = modal.querySelector('[data-modal-custom-fields]');

      if (playersContainer) {
        playersContainer.innerHTML = '';
        const playersList = product.players || [];

        if (playersList.length > 0) {
          playersList.forEach(function (player) {
            const pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'combo-player-pill';
            pill.setAttribute('data-player-value', player);
            pill.textContent = player;
            playersContainer.appendChild(pill);
          });

          // Also add a "Custom" pill
          const customPill = document.createElement('button');
          customPill.type = 'button';
          customPill.className = 'combo-player-pill';
          customPill.setAttribute('data-player-value', 'Custom');
          customPill.textContent = 'Custom Name/No.';
          playersContainer.appendChild(customPill);

          playersContainer.style.display = 'flex';
        } else {
          playersContainer.style.display = 'none';
        }
      }

      if (customFields) {
        customFields.classList.remove('is-hidden');
        const nameInput = customFields.querySelector('[data-input-name]');
        const numberInput = customFields.querySelector('[data-input-number]');
        if (nameInput) nameInput.value = '';
        if (numberInput) numberInput.value = '';
      }

      modal.classList.add('is-open');
    } catch (err) {
      console.error('[ComboBuilder] Error opening modal:', err);
    }
  }

  /**
   * Closes the Customization Modal
   * @param {HTMLElement} root
   */
  function closeModal(root) {
    try {
      if (!root) return;
      const modal = root.querySelector('[data-combo-modal]');
      if (modal) {
        modal.classList.remove('is-open');
      }
      comboState.activeModalProduct = null;
      comboState.selectedVariantPrice = 0;
      comboState.selectedVariantId = '';
      comboState.selectedSize = '';
    } catch (e) {
      // defensive
    }
  }

  /**
   * Adds the currently customized product to the next available combo slot
   * @param {HTMLElement} root
   */
  function handleConfirmSlot(root) {
    try {
      if (!comboState.activeModalProduct) return;

      let finalName = '';
      let finalNumber = '';

      if (comboState.selectedPlayerPreset) {
        const parsed = parsePlayerString(comboState.selectedPlayerPreset);
        finalName = parsed.name;
        finalNumber = parsed.number;
      } else {
        finalName = (comboState.customName || '').trim().toUpperCase();
        finalNumber = (comboState.customNumber || '').trim();
      }

      if (finalName && (!finalNumber || finalName.includes('#'))) {
        const parsed = parsePlayerString(finalName);
        if (parsed.number) {
          finalName = parsed.name;
          finalNumber = parsed.number;
        }
      }

      let variantPrice = comboState.selectedVariantPrice;
      if (!variantPrice && comboState.activeModalProduct && comboState.activeModalProduct.variants) {
        const found = comboState.activeModalProduct.variants.find(function (v) {
          return String(v.id) === String(comboState.selectedVariantId);
        });
        if (found && found.price) {
          variantPrice = Number(found.price);
        }
      }
      if (!variantPrice && comboState.activeModalProduct) {
        variantPrice = Number(comboState.activeModalProduct.price) || 0;
      }

      const itemPayload = {
        productId: comboState.activeModalProduct.id,
        variantId: comboState.selectedVariantId,
        title: comboState.activeModalProduct.title,
        url: comboState.activeModalProduct.url || '',
        image: comboState.activeModalProduct.image,
        price: variantPrice,
        size: comboState.selectedSize,
        playerName: finalName,
        playerNumber: finalNumber
      };

      comboState.slots.push(itemPayload);
      saveComboSlotsToStorage();
      closeModal(root);
      updateDockUI(root);
    } catch (err) {
      console.error('[ComboBuilder] Error confirming slot:', err);
    }
  }

  /**
   * Handles adding entire combo to the Shopify Cart
   * @param {HTMLElement} root
   * @param {HTMLButtonElement} btn
   */
  async function handleAddComboToCart(root, btn) {
    try {
      const count = comboState.slots.length;
      if (count < 1) return;

      if (btn) {
        btn.disabled = true;
        const textSpan = btn.querySelector('[data-btn-text]');
        if (textSpan) textSpan.textContent = 'ADDING COMBO...';
      }

      const discountPct = getCurrentDiscount(count);
      const discountCode = count >= 3 ? 'FINALFORM20' : count === 2 ? 'LEVELUP15' : count === 1 ? 'WEEBMODE10' : '';
      if (discountCode) {
        try {
          await fetch(`/discount/${discountCode}`);
        } catch (_) {}
      }

      const comboId = 'combo_' + Date.now();
      const comboName = count >= 2 ? `Anime Combo (${count} Jerseys)` : 'Custom Anime Jersey';

      const itemsPayload = comboState.slots.map(function (slot) {
        const properties = {
          'Combo Pack': comboName,
          '_combo_id': comboId
        };
        if (discountPct > 0) {
          properties['_discount'] = `${discountPct}% OFF`;
        }

        // Exact same way as normal add-to-cart: Jersey Name and Jersey Number as two separate clean properties
        let slotName = (slot.playerName || '').trim();
        let slotNumber = (slot.playerNumber || '').trim();

        if (slotName.includes('#')) {
          const parsed = parsePlayerString(slotName);
          slotName = parsed.name;
          if (!slotNumber) slotNumber = parsed.number;
        }

        if (slotName) {
          properties['Jersey Name'] = slotName.toUpperCase();
        }
        if (slotNumber) {
          properties['Jersey Number'] = slotNumber;
        }

        return {
          id: slot.variantId,
          quantity: 1,
          properties: properties
        };
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: itemsPayload,
          sections: 'cart-drawer-section',
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add combo to cart');
      }

      const addData = await response.json();

      // 1. Morph or update cart drawer section with fresh HTML
      let drawerHtml = addData?.sections?.['cart-drawer-section'];
      if (!drawerHtml) {
        try {
          const secRes = await fetch('/?sections=cart-drawer-section', {
            headers: { 'Accept': 'application/json' }
          });
          const sec = await secRes.json();
          drawerHtml = sec?.['cart-drawer-section'];
        } catch (_) {}
      }

      if (drawerHtml) {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(drawerHtml, 'text/html');
          const currentSection = document.getElementById('shopify-section-cart-drawer-section');
          const newSection = doc.getElementById('shopify-section-cart-drawer-section');

          if (currentSection && newSection) {
            currentSection.innerHTML = newSection.innerHTML;
          } else {
            const currentInner = document.querySelector('.cart-drawer__inner');
            const newInner = doc.querySelector('.cart-drawer__inner');
            if (currentInner && newInner) {
              currentInner.innerHTML = newInner.innerHTML;
              currentInner.className = newInner.className;
            }
          }
        } catch (e) {
          console.warn('[ComboBuilder] Cart drawer HTML injection failed:', e);
        }
      }

      // 2. Update cart counts in header / badges
      try {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const count = cart.item_count || 0;

        document.querySelectorAll('.cart-bubble, .theme-drawer__badge').forEach(function (el) {
          el.classList.remove('visually-hidden', 'hidden');
        });
        document.querySelectorAll(
          '.cart-bubble__text-count, .theme-drawer__badge, [data-cart-count], [data-cart-item-count], .cart__count, .cart-items-count, span.cart-count, .site-header__cart-count, .js-cart-count, .cart_count, .mini-cart__count, .cart-link__count, .icon-cart__count, .cart-quantity, .CartCount, #CartCount'
        ).forEach(function (el) {
          el.textContent = count.toString();
          el.removeAttribute('hidden');
          el.classList.remove('hidden', 'visually-hidden');
        });

        // 3. Dispatch Shopify & Theme events so any reactive cart widgets update
        const evt = new CustomEvent('shopify:cart:lines-update', { bubbles: true, cancelable: true });
        evt.action = 'add';
        evt.context = 'cart';
        evt.lines = [];
        evt.promise = Promise.resolve({
          cart: { totalQuantity: count, item_count: count, items: cart.items },
          detail: { cart: cart, itemCount: count, sections: addData?.sections || {}, source: 'product-form-component', didError: false }
        });
        document.dispatchEvent(evt);

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.body.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
        document.body.dispatchEvent(new CustomEvent('cart-update', { bubbles: true, detail: { cart: cart } }));
      } catch (_) {}

      // 4. Close mobile popup & Open Theme Cart Drawer
      const dock = root.querySelector('[data-combo-dock]');
      if (dock) dock.classList.remove('is-popup-open');

      const drawer = document.querySelector('theme-drawer#cart-drawer') || document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else if (window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.openCart === 'function') {
        window.Shopify.actions.openCart();
      } else {
        window.location.href = '/cart';
      }

      if (btn) {
        const textSpan = btn.querySelector('[data-btn-text]');
        if (textSpan) textSpan.textContent = 'ADDED TO CART!';
        setTimeout(function () {
          clearComboSlotsFromStorage();
          comboState.slots = [];
          updateDockUI(root);
        }, 1500);
      }
    } catch (err) {
      console.error('[ComboBuilder] Add to cart error:', err);
      alert('Could not add combo to cart. Please check stock and try again.');
      if (btn) {
        btn.disabled = false;
        const textSpan = btn.querySelector('[data-btn-text]');
        if (textSpan) textSpan.textContent = 'ADD COMBO TO CART';
      }
    }
  }

  /**
   * Smoothly scrolls carousel tracks with edge-wrapping and cross-browser fallbacks
   * @param {HTMLElement} btn
   * @param {string} action
   */
  function scrollCarousel(btn, action) {
    try {
      if (!btn) return;
      const row = btn.closest('[data-carousel-row]');
      if (!row) return;

      const track = row.querySelector('[data-carousel-track]');
      if (!track) return;

      const card = track.querySelector('.combo-product-card--carousel');
      const cardWidth = card ? (card.offsetWidth + 14) : 240;
      const scrollDistance = cardWidth * 1.5;
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const currentLeft = track.scrollLeft;

      if (maxScroll <= 0) return; // No overflow to scroll

      let targetLeft = currentLeft;
      if (action === 'carousel-next') {
        if (currentLeft >= maxScroll - 20) {
          targetLeft = 0; // Wrap around to start
        } else {
          targetLeft = Math.min(maxScroll, currentLeft + scrollDistance);
        }
      } else if (action === 'carousel-prev') {
        if (currentLeft <= 20) {
          targetLeft = maxScroll; // Wrap around to end
        } else {
          targetLeft = Math.max(0, currentLeft - scrollDistance);
        }
      }

      // 1. Try smooth scroll
      try {
        track.scrollTo({ left: targetLeft, behavior: 'smooth' });
      } catch (err) {
        track.scrollLeft = targetLeft;
      }

      // 2. Fallback check: if WebKit scroll-snap aborts smooth scroll, ensure scrollLeft updates
      setTimeout(function () {
        if (Math.abs(track.scrollLeft - currentLeft) < 5 && currentLeft !== targetLeft) {
          track.scrollLeft = targetLeft;
        }
      }, 70);
    } catch (e) {
      console.error('[ComboBuilder] Carousel scroll error:', e);
    }
  }

  /**
   * Initializes event delegation for the combo builder section
   * @param {HTMLElement} root
   */
  function initComboBuilder(root) {
    if (!root) return;

    // Read discounts from data attributes
    comboState.discount1 = parseInt(root.getAttribute('data-discount-1') || '10', 10);
    comboState.discount2 = parseInt(root.getAttribute('data-discount-2') || '15', 10);
    comboState.discount3 = parseInt(root.getAttribute('data-discount-3') || '20', 10);

    try {
      document.body.classList.add('has-combo-builder');
    } catch (_) {}

    // Dynamically calculate and track header offset
    function updateHeaderOffset() {
      try {
        const header = document.querySelector('header-component, #header-component, .header-section, #header-group');
        if (header) {
          const rect = header.getBoundingClientRect();
          const height = Math.max(70, Math.round(rect.height || header.offsetHeight || 0));
          root.style.setProperty('--cb-header-offset', `${height + 24}px`);
        }
      } catch (_) {}
    }

    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset, { passive: true });

    // Load saved combo slots from storage
    loadComboSlotsFromStorage();

    // Initial dock render
    updateDockUI(root);

    // Direct binding for carousel prev/next buttons
    const carouselBtns = root.querySelectorAll('[data-action="carousel-prev"], [data-action="carousel-next"]');
    carouselBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const action = btn.getAttribute('data-action');
        scrollCarousel(btn, action);
      });
    });

    // Mobile Touch Swipe Gesture: Swipe UP on dock to open popup, Swipe DOWN to close
    const dockEl = root.querySelector('[data-combo-dock]');
    if (dockEl) {
      let touchStartY = 0;
      let touchStartX = 0;
      let isTrackingTouch = false;

      dockEl.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        touchStartY = e.touches[0].clientY;
        touchStartX = e.touches[0].clientX;
        isTrackingTouch = true;
      }, { passive: true });

      dockEl.addEventListener('touchend', function (e) {
        if (!isTrackingTouch) return;
        isTrackingTouch = false;
        const touchEndY = e.changedTouches[0].clientY;
        const touchEndX = e.changedTouches[0].clientX;
        const deltaY = touchEndY - touchStartY;
        const deltaX = Math.abs(touchEndX - touchStartX);

        // Ignore horizontal scroll swipes
        if (deltaX > Math.abs(deltaY)) return;

        const isPopupOpen = dockEl.classList.contains('is-popup-open');

        // Swipe UP on dock (drag upward > 25px) -> open popup upward
        if (!isPopupOpen && deltaY < -25) {
          dockEl.classList.add('is-popup-open');
          return;
        }

        // Swipe DOWN on opened popup header / handle (drag downward > 35px) -> close popup
        if (isPopupOpen && deltaY > 35) {
          const target = e.target;
          if (target && target.closest && target.closest('.combo-dock__handle, .combo-dock__header, .combo-dock__peek-handle, .combo-meter')) {
            dockEl.classList.remove('is-popup-open');
          }
        }
      }, { passive: true });
    }

    // Click Delegation
    root.addEventListener('click', function (e) {
      // Click on product card anywhere outside action buttons navigates to product
      const productCard = e.target.closest('[data-product-card]');
      if (productCard && !e.target.closest('button, [data-action], a')) {
        const productUrl = productCard.getAttribute('data-product-url');
        if (productUrl) {
          window.location.href = productUrl;
          return;
        }
      }

      const target = e.target.closest('button, [data-action]');
      if (!target) return;

      const action = target.getAttribute('data-action');

      // 0. Mobile Combo Popup Open / Close
      if (action === 'open-combo-popup') {
        const dock = root.querySelector('[data-combo-dock]');
        if (dock) dock.classList.add('is-popup-open');
        return;
      }

      if (action === 'close-combo-popup') {
        const dock = root.querySelector('[data-combo-dock]');
        if (dock) dock.classList.remove('is-popup-open');
        return;
      }

      // 1. Scroll to products when tapping "+ Add Another" slot
      if (action === 'scroll-to-products') {
        const dock = root.querySelector('[data-combo-dock]');
        if (dock) dock.classList.remove('is-popup-open');
        const firstTrack = root.querySelector('[data-carousel-track]');
        if (firstTrack) {
          firstTrack.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        return;
      }

      // 2. Carousel Prev / Next Horizontal Scrolling
      if (action === 'carousel-prev' || action === 'carousel-next') {
        e.preventDefault();
        e.stopPropagation();
        scrollCarousel(target, action);
        return;
      }

      // 3. Add to Combo Button (Opens Modal)
      if (action === 'open-add-modal') {
        const card = target.closest('[data-product-card]');
        if (!card) return;

        const jsonScript = card.querySelector('[data-product-json]');
        if (!jsonScript) return;

        try {
          const productData = JSON.parse(jsonScript.textContent);
          openCustomizationModal(root, productData);
        } catch (err) {
          console.error('[ComboBuilder] Invalid product JSON:', err);
        }
        return;
      }

      // 4. Modal Close
      if (action === 'close-modal') {
        closeModal(root);
        return;
      }

      // 5. Size Selection Pill
      if (target.classList.contains('combo-size-pill')) {
        const sizePills = root.querySelectorAll('.combo-size-pill');
        sizePills.forEach(function (p) { p.classList.remove('is-selected'); });
        target.classList.add('is-selected');

        comboState.selectedVariantId = target.getAttribute('data-variant-id') || '';
        comboState.selectedSize = target.getAttribute('data-size-title') || '';

        const priceAttr = target.getAttribute('data-variant-price');
        let variantPrice = priceAttr ? Number(priceAttr) : 0;
        if (!variantPrice && comboState.activeModalProduct && comboState.activeModalProduct.variants) {
          const found = comboState.activeModalProduct.variants.find(function (v) {
            return String(v.id) === String(comboState.selectedVariantId);
          });
          if (found && found.price) {
            variantPrice = Number(found.price);
          }
        }
        if (!variantPrice && comboState.activeModalProduct) {
          variantPrice = Number(comboState.activeModalProduct.price) || 0;
        }

        comboState.selectedVariantPrice = variantPrice;

        const modal = root.querySelector('[data-combo-modal]');
        if (modal) {
          const priceEl = modal.querySelector('[data-modal-price]');
          if (priceEl && variantPrice > 0) {
            priceEl.textContent = formatCurrency(variantPrice);
          }
        }
        return;
      }

      // 6. Player Preset Pill
      if (target.classList.contains('combo-player-pill')) {
        const playerPills = root.querySelectorAll('.combo-player-pill');
        playerPills.forEach(function (p) { p.classList.remove('is-selected'); });
        target.classList.add('is-selected');

        const val = target.getAttribute('data-player-value') || '';
        const nameInput = root.querySelector('[data-input-name]');
        const numberInput = root.querySelector('[data-input-number]');

        if (val.toLowerCase() === 'custom') {
          comboState.selectedPlayerPreset = '';
          if (nameInput) {
            nameInput.value = '';
            nameInput.focus();
          }
          if (numberInput) {
            numberInput.value = '';
          }
        } else {
          comboState.selectedPlayerPreset = val;
          const parsed = parsePlayerString(val);
          if (nameInput) nameInput.value = parsed.name;
          if (numberInput) numberInput.value = parsed.number;
          comboState.customName = parsed.name;
          comboState.customNumber = parsed.number;
        }
        return;
      }

      // 7. Modal Confirm Button
      if (action === 'confirm-slot') {
        const nameInput = root.querySelector('[data-input-name]');
        const numberInput = root.querySelector('[data-input-number]');
        if (nameInput && nameInput.value.trim()) {
          comboState.customName = nameInput.value.trim().toUpperCase();
        }
        if (numberInput && numberInput.value.trim()) {
          comboState.customNumber = numberInput.value.trim();
        }

        handleConfirmSlot(root);
        return;
      }

      // 8. Remove Slot Item
      if (action === 'remove-slot') {
        e.preventDefault();
        e.stopPropagation();
        const index = parseInt(target.getAttribute('data-index') || '0', 10);
        if (!isNaN(index) && index >= 0 && index < comboState.slots.length) {
          comboState.slots.splice(index, 1);
          saveComboSlotsToStorage();
          updateDockUI(root);
        }
        return;
      }

      // 9. Add Entire Combo To Cart
      if (action === 'submit-combo') {
        handleAddComboToCart(root, target);
        return;
      }
    });

    // Close modal when tapping backdrop
    const modal = root.querySelector('[data-combo-modal]');
    if (modal) {
      modal.addEventListener('click', function (e) {
        if (e.target === modal) {
          closeModal(root);
        }
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeModal(root);
        const dock = root.querySelector('[data-combo-dock]');
        if (dock && dock.classList.contains('is-popup-open')) {
          dock.classList.remove('is-popup-open');
        }
      }
    });
  }

  // Auto-initialize on load
  function init() {
    const builderElements = document.querySelectorAll('[data-combo-builder]');
    builderElements.forEach(function (el) {
      initComboBuilder(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
